/**
 * Pure model for the trip week strip. Props in, plain data out.
 *
 * Every date here is a CALENDAR date ("2026-12-09"), never an instant. All
 * arithmetic goes through Date.UTC on the y/m/d integers and reads back with
 * the UTC getters, so the viewer's timezone can never move a flight to the
 * neighbouring day. Inputs that arrive as ISO timestamps are cut to their
 * date part, not re-zoned.
 */

export interface CalendarFlight {
  date: string;
  role: "outbound" | "return";
  primary: boolean;
}

export interface CalendarEvent {
  date: string;
  /** Last day covered, inclusive. Omitted for a one-day event. */
  endDate?: string | null;
  title: string;
  /** Proposed from trip text, not yet written to the itinerary. */
  unconfirmed?: boolean;
}

export interface CalendarMark {
  kind: "flight" | "event" | "alt";
  label: string;
  /** An event that is only proposed. */
  pending?: boolean;
}

export interface CalendarDay {
  date: string;
  /** 0 = Sunday. */
  weekday: number;
  dayOfMonth: number;
  marks: CalendarMark[];
  /** Between (and including) the primary outbound and return days. */
  inStay: boolean;
  today: boolean;
  /** Belongs to the trip window, as opposed to week padding. */
  inWindow: boolean;
}

export interface CalendarModel {
  weeks: CalendarDay[][];
  /** "row" = one continuous strip; "weeks" = whole-week rows for long trips. */
  layout: "row" | "weeks";
  caption: string;
  nights: number | null;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"];

/** "2026-12-09T00:00:00.000Z" -> "2026-12-09". Null for anything unparseable. */
export function toDay(value: string | null | undefined): string | null {
  const m = typeof value === "string" ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null;
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function ordinal(day: string): number {
  const [y, m, d] = day.split("-").map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
}

function fromOrdinal(n: number): string {
  const dt = new Date(n * 86_400_000);
  const p = (x: number) => String(x).padStart(2, "0");
  return `${dt.getUTCFullYear()}-${p(dt.getUTCMonth() + 1)}-${p(dt.getUTCDate())}`;
}

export function addDays(day: string, n: number): string {
  return fromOrdinal(ordinal(day) + n);
}

export function diffDays(a: string, b: string): number {
  return ordinal(b) - ordinal(a);
}

export function weekdayOf(day: string): number {
  return (((ordinal(day) + 4) % 7) + 7) % 7; // 1970-01-01 was a Thursday
}

/** "Wed Dec 9" */
export function shortDate(day: string): string {
  const [, m, d] = day.split("-").map(Number);
  return `${WEEKDAYS[weekdayOf(day)]} ${MONTHS[m - 1]} ${d}`;
}

function plural(n: number, word: string) {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

export function buildCalendar(input: {
  flights: CalendarFlight[];
  events: CalendarEvent[];
  today?: string | null;
  /** 0 = Sunday, 1 = Monday. */
  weekStart?: 0 | 1;
}): CalendarModel | null {
  const weekStart = input.weekStart ?? 0;
  const flights: CalendarFlight[] = [];
  for (const f of input.flights) {
    const date = toDay(f.date);
    if (date) flights.push({ ...f, date });
  }
  const events: Array<{ date: string; end: string; title: string; pending: boolean }> = [];
  for (const e of input.events) {
    const date = toDay(e.date);
    if (!date) continue;
    const end = toDay(e.endDate);
    events.push({ date, end: end && diffDays(date, end) > 0 ? end : date, title: e.title, pending: !!e.unconfirmed });
  }
  if (flights.length === 0 && events.length === 0) return null;

  const marks = new Map<string, CalendarMark[]>();
  const put = (day: string, mark: CalendarMark) => {
    const list = marks.get(day) ?? [];
    list.push(mark);
    marks.set(day, list);
  };

  const primaries = flights.filter((f) => f.primary);
  const out = primaries.filter((f) => f.role === "outbound").map((f) => f.date).sort()[0] ?? null;
  const ret = primaries.filter((f) => f.role === "return").map((f) => f.date).sort().reverse()[0] ?? null;

  for (const f of flights) {
    const word = f.role === "outbound" ? "Outbound flight" : "Return flight";
    put(f.date, f.primary
      ? { kind: "flight", label: word }
      : { kind: "alt", label: `${word}, alternative date` });
  }
  for (const e of events) {
    for (let d = e.date; diffDays(d, e.end) >= 0; d = addDays(d, 1)) put(d, { kind: "event", label: e.title, pending: e.pending });
  }

  // A real flight on a day outranks a dotted alternative for the same day.
  for (const [day, list] of marks) {
    if (list.some((m) => m.kind === "flight")) marks.set(day, list.filter((m) => m.kind !== "alt"));
  }

  const days = [...marks.keys()].sort();
  const first = days[0];
  const last = days[days.length - 1];
  const today = toDay(input.today ?? null);

  const MAX_ROW_DAYS = 16;
  const rowStart = addDays(first, -1);
  const rowLen = diffDays(first, last) + 3;
  const mk = (date: string): CalendarDay => ({
    date,
    weekday: weekdayOf(date),
    dayOfMonth: Number(date.slice(8)),
    marks: marks.get(date) ?? [],
    inStay: out !== null && ret !== null && date >= out && date <= ret,
    today: today === date,
    inWindow: date >= first && date <= last,
  });

  let shown: CalendarDay[][];
  const layout: "row" | "weeks" = rowLen <= MAX_ROW_DAYS ? "row" : "weeks";
  if (layout === "row") {
    shown = [Array.from({ length: rowLen }, (_, i) => mk(addDays(rowStart, i)))];
  } else {
    const startOfWeek = (d: string) => addDays(d, -((weekdayOf(d) - weekStart + 7) % 7));
    const weekCount = Math.floor(diffDays(startOfWeek(first), startOfWeek(last)) / 7) + 1;
    const weeks: CalendarDay[][] = [];
    for (let w = 0; w < weekCount; w++) {
      const ws = addDays(startOfWeek(first), w * 7);
      weeks.push(Array.from({ length: 7 }, (_, i) => mk(addDays(ws, i))));
    }
    // A long trip would be mostly empty rows: keep only the weeks with something on them.
    shown = weeks.filter((r) => r.some((d) => d.marks.length > 0));
  }

  const nights = out && ret && diffDays(out, ret) > 0 ? diffDays(out, ret) : null;
  const parts: string[] = [];
  const startDay = out ?? primaries.map((f) => f.date).sort()[0] ?? null;
  if (startDay && ret && startDay !== ret) parts.push(`${shortDate(startDay)} → ${shortDate(ret)}`);
  else if (startDay) parts.push(shortDate(startDay));
  else if (ret) parts.push(shortDate(ret));
  if (nights !== null) parts.push(plural(nights, "night"));
  const byDay = new Map<string, string[]>();
  for (const e of events) byDay.set(e.date, [...(byDay.get(e.date) ?? []), e.pending ? `${e.title} (unconfirmed)` : e.title]);
  for (const d of [...byDay.keys()].sort()) parts.push(`${byDay.get(d)!.join(" + ")} ${shortDate(d)}`);

  return { weeks: shown, layout, caption: parts.join(" · "), nights };
}
